import { etagFromRow, get, getVersioned, post, seg } from '../client';
import type { ListParams, Page } from '../types/common';
import type {
  Account, Charge, ChargeListParams, CodingQuery, CreateCodingQueryRequest, CreatePaymentRequest, FinancialReport, JournalEntry, Payment, RefundRequest, Superbill, SuperbillQueue,
} from '../types/billing';

export const billingApi = {
  superbill: (encounterId: string) => getVersioned<Superbill>(`/encounters/${seg(encounterId)}/superbill`),
  /** Charge trigger after a signed encounter. */
  finalizeSuperbill: (encounterId: string, etag: string | null) =>
    post<Superbill>(`/encounters/${seg(encounterId)}/superbill:finalize`, {}, { ifMatch: etag, idempotent: true, purpose: 'PAYMENT' }),
  /** Sorted newest service date first (the backend pages in id order). */
  charges: async (p: ListParams & ChargeListParams = {}) => byDesc(await get<Page<Charge>>('/charges', { ...p }, { purpose: 'PAYMENT' }), (c) => `${c.serviceDate}|${c.createdAt}`),
  /** GET /charges/{id} with its ETag. */
  charge: (id: string) => getVersioned<Charge>(`/charges/${seg(id)}`, undefined, { purpose: 'PAYMENT' }),
  /**
   * Charge commands need If-Match: pass `billingEtag(charge)` (W/"<rowVersion>") from the listed charge. When omitted the
   * current ETag is fetched first (never a cached one). Each command returns the charge with its new rowVersion.
   */
  holdCharge: async (id: string, reason: string, etag?: string | null) =>
    post<Charge>(`/charges/${seg(id)}:hold`, { reason }, { ifMatch: etag ?? (await chargeEtag(id)), idempotent: true, purpose: 'PAYMENT' }),
  releaseCharge: async (id: string, reason: string, etag?: string | null) =>
    post<Charge>(`/charges/${seg(id)}:release`, { reason }, { ifMatch: etag ?? (await chargeEtag(id)), idempotent: true, purpose: 'PAYMENT' }),
  /** Never a destructive edit: creates a linked reversal. */
  reverseCharge: async (id: string, reason: string, etag?: string | null) =>
    post<Charge>(`/charges/${seg(id)}:reverse`, { reason }, { ifMatch: etag ?? (await chargeEtag(id)), idempotent: true, purpose: 'PAYMENT' }),
  codingQueries: (p: ListParams & { status?: string } = {}) => get<Page<CodingQuery>>('/coding-queries', { ...p }),
  createCodingQuery: (body: CreateCodingQueryRequest) => post<CodingQuery>('/coding-queries', body, { idempotent: true, purpose: 'PAYMENT' }),
  /** GET /coding-queries/{id} with its ETag (needed as If-Match to respond). */
  codingQuery: (id: string) => getVersioned<CodingQuery>(`/coding-queries/${seg(id)}`),
  /**
   * Addressed provider answers a coding query (R-116). Needs If-Match: pass the ETag from `codingQuery(id)` or
   * `W/"<rowVersion>"` from the list; when omitted the current ETag is fetched first.
   */
  respondCodingQuery: async (id: string, response: string, etag?: string | null) => {
    // Fallback fetch; if it fails the command itself reports the real problem (403/404/428).
    const ifMatch = etag ?? (await getVersioned<CodingQuery>(`/coding-queries/${seg(id)}`).then((r) => r.etag, () => null));
    return post<CodingQuery>(`/coding-queries/${seg(id)}:respond`, { response }, { ifMatch, idempotent: true });
  },
  account: (patientId: string) => get<Account>(`/accounts/${seg(patientId)}`, undefined, { purpose: 'PAYMENT' }),
  journal: (accountId: string, p: ListParams = {}) => get<Page<JournalEntry>>('/ledger/journal', { accountId, ...p }, { purpose: 'PAYMENT' }),
  /**
   * Every journal line of an account (bounded), ordered by posting time then transaction, so one financial event is never
   * split across pages (the backend pages lines in id order). `nextCursor` is non-null if the bound was hit.
   */
  journalAll: async (accountId: string, maxPages = 20): Promise<Page<JournalEntry>> => {
    const items: JournalEntry[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < maxPages; i++) {
      const page: Page<JournalEntry> = await get<Page<JournalEntry>>('/ledger/journal', { accountId, limit: 100, cursor: cursor ?? undefined }, { purpose: 'PAYMENT' });
      items.push(...page.items);
      cursor = page.nextCursor;
      if (!cursor) break;
    }
    return { items: sortJournal(items), nextCursor: cursor };
  },
  payments: async (accountId: string) => byDesc(await get<Page<Payment>>('/payments', { accountId, limit: 100 }, { purpose: 'PAYMENT' }), (x) => x.postedAt),
  /** The backend body is {accountId, amount, method:{type, token}} only — a note is rejected (422), so none is sent. */
  postPayment: (body: CreatePaymentRequest) =>
    post<Payment>('/payments', { accountId: body.accountId, amount: body.amount, method: { type: body.method.type, token: body.method.token } }, { idempotent: true, purpose: 'PAYMENT' }),
  /**
   * Segregation of duties: the poster of a payment cannot refund it (403). Step-up and If-Match required: pass
   * `billingEtag(payment)`; when omitted the current ETag is fetched first.
   */
  refund: async (paymentId: string, body: RefundRequest, etag?: string | null) =>
    post<Payment>(`/payments/${seg(paymentId)}:refund`, body, {
      ifMatch: etag ?? (await getVersioned<Payment>(`/payments/${seg(paymentId)}`, undefined, { purpose: 'PAYMENT' }).then((r) => r.etag, () => null)),
      idempotent: true,
      purpose: 'PAYMENT',
      stepUpLabel: 'Refund payment',
    }),
  /* ---- Additive endpoints (documented in the revenue report) ---- */
  /** GET /superbills?queue= — composed superbill summaries for billable encounters (work queues) */
  superbills: (p: ListParams & { queue?: SuperbillQueue; patientId?: string } = {}) => get<Page<Superbill>>('/superbills', { ...p }, { purpose: 'PAYMENT' }),
  /** GET /accounts?q= — accounts with balances */
  accounts: (p: ListParams & { q?: string; balance?: 'open' | 'all' } = {}) => get<Page<Account>>('/accounts', { ...p }, { purpose: 'PAYMENT' }),
  /** Audited reveal of the full account number */
  revealAccountNumber: (patientId: string) => get<{ accountNumber: string }>(`/accounts/${seg(patientId)}/account-number`, undefined, { purpose: 'PAYMENT' }),
  /**
   * Billing data is released for PAYMENT (OPERATIONS is denied by policy and silently zeroes the report). The report may
   * still be partial: `restricted` / `complete` say how many accounts were withheld.
   */
  financialReport: (from: string, to: string) => get<FinancialReport>('/reports/financial', { from, to }, { purpose: 'PAYMENT' }),
};

/** W/"<rowVersion>" for a billing aggregate from a list/detail DTO (rowVersion, else the mock's version). */
export const billingEtag = (row: { rowVersion?: string | number | null; version?: number | null } | null | undefined) =>
  etagFromRow(row?.rowVersion ?? row?.version ?? null);

async function chargeEtag(id: string): Promise<string | null> {
  return getVersioned<Charge>(`/charges/${seg(id)}`, undefined, { purpose: 'PAYMENT' }).then((r) => r.etag, () => null);
}

const LEDGER_ORDER: Record<string, number> = { ar_insurance: 0, ar_patient: 1, revenue: 2, cash: 3, adjustments: 4 };
/** Stable journal order: posting time, then transaction, then debit lines before credit lines, then ledger. */
export function sortJournal(items: JournalEntry[]): JournalEntry[] {
  // Keep a transaction together even if its lines carry slightly different timestamps: order by the txn's first line.
  // Equal times keep the server's transaction order (first appearance), so ties never reshuffle between renders.
  const txnAt = new Map<string, string>();
  const firstSeen = new Map<string, number>();
  items.forEach((j, i) => {
    const k = j.txnId ?? j.id;
    const cur = txnAt.get(k);
    if (!cur || j.at < cur) txnAt.set(k, j.at);
    if (!firstSeen.has(k)) firstSeen.set(k, i);
  });
  return [...items].sort((a, b) => {
    const ka = a.txnId ?? a.id;
    const kb = b.txnId ?? b.id;
    const ta = txnAt.get(ka)!;
    const tb = txnAt.get(kb)!;
    if (ta !== tb) return ta < tb ? -1 : 1;
    if (ka !== kb) return firstSeen.get(ka)! - firstSeen.get(kb)!;
    const da = a.debit ? 0 : 1;
    const db = b.debit ? 0 : 1;
    if (da !== db) return da - db;
    return (LEDGER_ORDER[a.ledger ?? ''] ?? 9) - (LEDGER_ORDER[b.ledger ?? ''] ?? 9) || (a.id < b.id ? -1 : 1);
  });
}

/** Stable newest-first order for one page (ties by id). */
function byDesc<T extends { id: string }>(page: Page<T>, key: (t: T) => string): Page<T> {
  return { ...page, items: [...(page.items ?? [])].sort((a, b) => (key(a) === key(b) ? (a.id < b.id ? 1 : -1) : key(a) < key(b) ? 1 : -1)) };
}
